import React, { useEffect, useState } from 'react';
import {
  ActivityLog,
  Flex,
  openNotificationWithIcon,
  Page,
  PermissionGuard,
  useApiFilters,
} from '@mss/mss-component-library';
import columns from './columns';
import { notification } from 'antd';
import { useTranslation } from 'react-i18next';
import Breadcrumbs from '../../../common/Breadcrumbs';
import { permissionSet } from '../../../authorization/util/permissionSets';
import ChangedValues from '../../components/ChangedValues';
import DetailModal from '../../components/DetailModal';
import RangePicker from '../../components/RangePicker';
import Table from 'antd/es/table';
import Api from '../../services/Api';
import PageSidebar from 'jsx/common/FilterSidebar';

const PAGE_SIZE = 10;

export default function List() {
  const [activityLogs, setActivityLogs] = useState<{
    count: number;
    results: ActivityLog[];
  }>({ count: 0, results: [] });
  const [loading, setLoading] = useState<boolean>(true);

  const [t] = useTranslation(['activityLog', 'common']);
  const [modal, setModal] = useState<ActivityLog | null>(null);

  const [filtersLoading, setFiltersLoading] = useState(false);

  const filterApi = useApiFilters(t, activityLogs.count, PAGE_SIZE);

  useEffect(() => {
    async function fetchData() {
      if (!filterApi.urlParams) return;
      setLoading(true);
      try {
        const data = await Api.fetchActivityLogs(filterApi.urlParams);
        setActivityLogs(data);
      } catch (e) {
        openNotificationWithIcon(
          notification.error,
          t('Unable to get activitylog data'),
          String(e)
        );
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [filterApi.urlParams, t]);

  useEffect(() => {
    async function fetchData() {
      if (!filterApi.dateRangeUrlParams) return;
      setFiltersLoading(true);
      try {
        const data = await Api.fetchActivityLogFilters(
          filterApi.dateRangeUrlParams
        );
        filterApi.updateFilters(data);
      } catch (e) {
        openNotificationWithIcon(
          notification.error,
          t('Unable to get activitylog filters'),
          String(e)
        );
      } finally {
        setFiltersLoading(false);
      }
    }

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterApi.dateRangeUrlParams, filterApi.updateFilters, t]);

  return (
    <PermissionGuard codename={permissionSet.activityLogAny}>
      <Page
        breadCrumbs={<Breadcrumbs />}
        padding={false}
        actions={
          <RangePicker
            allowClear={false}
            onChange={filterApi.updateDateRange}
          />
        }
        main={
          <>
            <Table
              bordered={false}
              dataSource={activityLogs.results}
              loading={loading}
              columns={columns(t, x => setModal(x))}
              rowKey={'id'}
              pagination={filterApi.pagination}
              onChange={filterApi.onChange}
              expandable={{
                expandedRowRender: (record: any) =>
                  record.changed_values.length ? (
                    <ChangedValues changes={record.changed_values} t={t} />
                  ) : (
                    <Flex v="center" h="center" className="text-gray-500">
                      {t('No visible fields were changed.')}
                    </Flex>
                  ),
                rowExpandable: (record: any) =>
                  Array.isArray(record.changed_values) &&
                  record.changed_values.length > 0,
              }}
              expandRowByClick={true}
              size="small"
            />
            <DetailModal
              record={modal}
              closeModal={() => setModal(null)}
            ></DetailModal>
          </>
        }
        sidebar={
          <PageSidebar
            loading={filtersLoading}
            availableFilters={filterApi.filters}
            toggleFilter={filterApi.toggleFilter}
            search={{
              value: filterApi.search,
              set: filterApi.setSearch,
            }}
            selectedFilters={filterApi.selectedFilters}
            translationPrefix={['activityLog']}
          />
        }
      />
    </PermissionGuard>
  );
}
